import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, Subscription, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { BasePage } from '../../base.page';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import {
    ClientInterface,
    ContractInterface,
    GetAppropriatenessClientUseCase,
    GetContractUseCase,
    GetTestResultsUseCase,
    GetTestUseCase,
    IdInterface,
    TestResultInterface,
    Transaction,
    UpdateTestUseCase,
} from '../../../../core';
import { MainRoutes } from '../../../main-routes.enum';
import { ParentEvents } from '../../../utils/parent-events.service';
import { Test } from '../../../domain/data';
import { TestPage } from '../test.page';

@Component({
    templateUrl: './test-question.page.html',
    styleUrls: ['./test-question.page.scss'],
})
export class TestQuestionPage extends BasePage implements AfterViewInit, OnDestroy, OnInit {
    private backSubscription: Subscription;

    protected appropriatenessClient: ClientInterface;
    protected clientId: IdInterface;
    public contract: ContractInterface;
    public test: Test;
    public result: TestResultInterface;
    public isSuitability = false;

    public step = 0;
    public totalSteps = 7;
    public disabledOptions: number[] = [];
    public showingResume = false;
    public showingFullResume = false;
    public continueResume = false;

    protected questionPage: string = MainRoutes.TEST_QUESTION;
    protected resultPage: string = MainRoutes.TEST_RESULT;

    constructor(
        parentEvents: ParentEvents,
        protected dialog: MatDialog,
        protected getAppropriatenessClientUseCase: GetAppropriatenessClientUseCase,
        protected getContractUseCase: GetContractUseCase,
        protected getTestUseCase: GetTestUseCase,
        protected getTestResultsUseCase: GetTestResultsUseCase,
        protected route: ActivatedRoute,
        protected router: Router,
        protected testPage: TestPage,
        protected updateTestUseCase: UpdateTestUseCase
    ) {
        super(parentEvents);
        this.backSubscription = this.testPage.back.subscribe(() => {
            this.onBack();
        });
    }

    ngOnInit() {
        this.getStep();
        this.getTest();
    }

    private getStep() {
        this.route.params.subscribe((params) => {
            if (params.question) {
                this.step = Number(params.question);
            }
        });
    }

    protected getTest() {
        zip(
            this.getContractUseCase.bind().pipe(
                tap((transaction) => {
                    transaction.fold((contract) => (this.contract = contract));
                })
            ),
            this.getAppropriatenessClientUseCase.bind().pipe(
                tap((transaction) => {
                    transaction.fold((client) => {
                        this.appropriatenessClient = client;
                        this.clientId = this.appropriatenessClient.id;
                    });
                })
            )
        ).subscribe({
            complete: () => {
                if (this.contract && this.appropriatenessClient) {
                    this.isSuitability = this.contract.isAdvisoryModelSuitabilityOrGDC();

                    if (this.step === 0) {
                        this.testPage.hideBackButton();
                    } else {
                        this.testPage.showBackButton();
                    }

                    this.getTestUseCase
                        .bind({
                            clientId: this.clientId,
                            advisoryModel: this.contract.advisoryModel,
                        })
                        .pipe(
                            switchMap((transaction) =>
                                transaction.map(
                                    (test) =>
                                        this.isSuitability
                                            ? this.getTestResults().pipe(map(() => Transaction.success(test)))
                                            : of(Transaction.success(test)),
                                    (err) => of(Transaction.error(err.message))
                                )
                            )
                        )
                        .subscribe((transaction) =>
                            transaction.fold(
                                (test) => {
                                    this.test = test as Test;

                                    if (this.isSuitability) {
                                        this.totalSteps = this.test.questions.length;

                                        if (!!this.appropriatenessClient.mifidCompetenceId && !!this.appropriatenessClient.profileId) {
                                            this.showingFullResume = true;
                                        }
                                    } else if (!!this.appropriatenessClient.mifidCompetenceId) {
                                        if (this.contract.isAdvisoryModelGDC()) {
                                            this.router.navigateByUrl(MainRoutes.TEST_TRANSFER);
                                        } else {
                                            this.router.navigateByUrl(MainRoutes.FULL_DATA);
                                        }
                                    }
                                },
                                (err) =>
                                    this.dialog.open(ErrorDialog, {
                                        data: { text: err.message },
                                    })
                            )
                        );
                } else {
                    this.dialog.open(ErrorDialog);
                }
            },
        });
    }

    private getTestResults(): Observable<Transaction<TestResultInterface>> {
        return this.getTestResultsUseCase
            .bind({
                clientId: this.clientId,
                advisoryModel: this.contract.advisoryModel,
            })
            .pipe(
                tap((transaction) =>
                    transaction.fold(
                        (data) => (this.result = data),
                        (err) => this.dialog.open(ErrorDialog)
                    )
                )
            );
    }

    ngOnDestroy() {
        this.backSubscription.unsubscribe();
    }

    onNext() {
        if (this.step > this.totalSteps) {
            this.updateTest();
        } else {
            this.step++;
            this.calculateDisabledOptions();
            this.router.navigateByUrl(this.questionPage.replace(':question', String(this.step)));

            if (this.continueResume && this.step === 9) {
                this.testPage.hideBackButton();
            } else if (this.step > 0 && this.isSuitability) {
                this.testPage.showBackButton();
            } else if (this.step > 1) {
                this.testPage.showBackButton();
            }
        }

        this.parentEvents.resize();
        this.parentEvents.scrollUp();
    }

    protected updateTest() {
        this.test.advisoryModel = this.contract.advisoryModel;
        this.updateTestUseCase.bind({ clientId: this.clientId, test: this.test }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.router.navigateByUrl(this.resultPage);
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    private calculateDisabledOptions(onBack = false) {
        const question1 = this.test.questions[0];
        const question2 = this.test.questions[1];
        const question3 = this.test.questions[2];

        switch (this.step) {
            case 3:
                if (question1.options.length > 4 && question1.options[4].selected) {
                    this.disabledOptions = [0];
                }

                if (question2.options.length > 4 && (question2.options[3].selected || question2.options[4].selected)) {
                    this.disabledOptions = [0, 1];
                }

                break;
            case 5:
                if (question3.options.length > 2 && (question3.options[0].selected || question3.options[1].selected)) {
                    this.step = onBack ? 4 : 6;
                }

                break;
            default:
                this.disabledOptions = [];
        }
    }

    onBack() {
        if (this.step === 0 || (this.step === 1 && !this.isSuitability)) {
            return;
        }

        this.step--;
        this.calculateDisabledOptions(true);
        this.router.navigateByUrl(this.questionPage.replace(':question', encodeURIComponent(String(this.step))));

        this.test.questions.map((question, idx) => {
            if (this.step < 4 && idx > this.step - 1) {
                question.options.map((option) => {
                    option.selected = false;
                });

                question.groups.map((group) => {
                    group.options.map((option) => {
                        option.selected = false;
                    });
                });
            } else if (this.step === 4 && idx === 3) {
                question.groups.map((group, idxGroup) => {
                    group.options.map((option) => {
                        if (idxGroup > 4) {
                            option.selected = false;
                        }
                    });
                });
            } else if (this.step > 4 && idx > this.step - 2) {
                question.options.map((option) => {
                    option.selected = false;
                });
            }
        });

        if (this.step <= 0 || (this.continueResume && this.step === 9) || (this.step === 1 && !this.isSuitability)) {
            this.testPage.hideBackButton();
        }

        this.parentEvents.resize();
        this.parentEvents.scrollUp();
    }

    onResume() {
        if (this.showingFullResume) {
            if (this.contract.isAdvisoryModelGDC()) {
                this.router.navigateByUrl(MainRoutes.TEST_TRANSFER);
            } else {
                this.router.navigateByUrl(MainRoutes.FULL_DATA);
            }
        } else {
            this.resultPage = MainRoutes.TEST_RESULT_SUITABILITY;
            this.continueResume = true;
            this.step = 8;
            this.test.loadResults(this.result);
            this.onNext();
        }
    }

    onRepeat() {
        this.resultPage = MainRoutes.TEST_RESULT + '?suitability=1';
        this.test.reset();
        this.onNext();
    }
}
